import type { EditorConfig } from './types';
import {
   CustomCharacterCount,
   SessionStorage,
   OriginalState,
   Submit,
   ValidateContent,
} from './extensions';
import { Document } from '@tiptap/extension-document';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { History } from '@tiptap/extension-history';
import { Content, Editor, Extensions } from '@tiptap/react';
import { buildExtensionsFromWikitags } from './build_extensions';

const getDefaultExtensions = (editorConfig: EditorConfig): Extensions => {
   const { editorId, content, characterCountConfig, submitHandler, fallbackHandler } = editorConfig;

   const extensions: Extensions = [
      Document,
      Paragraph,
      Text,
      History,
      SessionStorage.configure({
         editorId: editorId,
         autosaveWait: 200,
      }),
      OriginalState.configure({}),
   ];

   if (content) {
      extensions.push(
         ValidateContent.configure({
            parsedContent: content,
            fallbackHandler,
         })
      );
   }

   if (characterCountConfig) {
      extensions.push(
         CustomCharacterCount.configure({
            minCharacters: characterCountConfig.minCharacters,
            limit: characterCountConfig.maxCharacters,
         })
      );
   }

   if (submitHandler) {
      extensions.push(
         Submit.configure({
            submit: submitHandler,
         })
      );
   }

   return extensions;
};

const parseEditorContent = (content?: Content): Content => {
   if (!content) {
      return '';
   }

   if (typeof content != 'string') {
      return content;
   }

   try {
      return JSON.parse(content);
   } catch (e) {
      return content;
   }
};

export const buildEditor = (editorConfig: EditorConfig) => {
   const parsedContent = parseEditorContent(editorConfig.content);
   const parsedEditorConfig = { ...editorConfig, content: parsedContent };

   const extensions = [
      ...getDefaultExtensions(parsedEditorConfig),
      ...buildExtensionsFromWikitags(parsedEditorConfig),
   ];

   const editor = new Editor({
      extensions: extensions,
      content: parsedContent,
   });

   return editor;
};
